import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import queryStringUtil from 'query-string';
import Link from '../base_link/component';
import Icon from '../base_svg-icon/component';
import { stripHTMLByValue, getDevice } from '@utils';
import { paginationTag } from '@utils/analytics';
import Context from '@context/search-results';
import Text from '@text';
import './styles/pagination.scss';

/**
 * Paging component.
 *
 * This is a subcomponent of resultcontainer, but can also be used as standalone paging component.
 *
 */
class Pagination extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentPage: props.currentPage >= 0 ? props.currentPage : 0,
      totalPages: props.totalPages >= 0 ? props.totalPages : 0,
      isMobile: getDevice().isMobile,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDevice);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDevice);
  }

  updateDevice = () => {
    this.setState({
      isMobile: getDevice().isMobile,
    });
  };

  /**
   * Check if there is a querystring,
   * Add the query var to it.
   * Replace the query parameter for the paging.
   *
   * @param index
   * @returns {string}
   */
  getUrl(index) {
    const queryStringStart = this.url.indexOf('?');
    let queryObj = {};
    let baseUrl = this.url;
    if (queryStringStart !== -1) {
      const queryString = this.url.substr(queryStringStart + 1);
      queryObj = queryStringUtil.parse(queryString);
      stripHTMLByValue(queryObj);
      baseUrl = this.url.substr(0, queryStringStart);
    }
    queryObj[this.queryVar] = index;
    const newQueryString = queryStringUtil.stringify(queryObj);
    return `${baseUrl}?${newQueryString}`;
  }

  /**
   * Click event on the paging
   *
   * @param event
   * @param index
   */
  onClick = (event, index) => {
    event.preventDefault();
    const currentPage = Math.max(0, Math.min(index, this.totalPages - 1));
    this.props.store.pushToDataLayer(
      paginationTag(`${this.url.length > 1 ? '' : window.location.href}${this.getUrl(currentPage)}`)
    );

    if (this.context.setPageRequest) {
      this.context.setPageRequest(currentPage + 1);
    }

    if (this.props.callBack) {
      this.props.callBack(currentPage);
    }
  };

  get currentPage() {
    const current = this.props.currentPage;
    return parseInt(current.toString().replace('.', ''), 10);
  }

  /**
   * Get number of pages
   * If number contains any digit group separators
   * they are removed (1.908.000 -> 1908000)
   */
  get totalPages() {
    const total = this.props.totalPages;
    return parseInt(total.toString().replace('.', ''), 10);
  }

  get url() {
    return this.props.url || window.location.href;
  }

  get queryVar() {
    return this.props.value || 'PageNumber';
  }

  /**
   * Create the pages in the component
   *
   * @returns {Array}
   */
  get pages() {
    const { currentPage, totalPages }  = this;
    const halfSpread = 2;
    const pages = [];

    let startPage = currentPage - halfSpread;
    let endPage = currentPage + halfSpread;

    if (startPage <= 0) {
      endPage = Math.min(totalPages, endPage - startPage + 1);
      startPage = 1;
    }

    if (endPage > totalPages) {
      startPage = Math.max(1, startPage - (endPage - totalPages));
      endPage = totalPages;
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(<a className={i === currentPage + 1 ? 'active' : ''} href={this.getUrl(i - 1)}
        onClick={e => this.onClick(e, i - 1)}>{i}</a>);
    }

    if (startPage > 1) {
      if (startPage > 2) {
        pages.unshift(<span className="ellipsis">...</span>);
      }
      pages.unshift(<a href={this.getUrl(0)} onClick={e => this.onClick(e, 0)}>{1}</a>);
    }

    if (endPage < totalPages) {
      if (totalPages - endPage > 1) {
        pages.push(<span className="ellipsis">...</span>);
      }
      pages.push(<a href={this.getUrl(totalPages - 1)}
        onClick={e => this.onClick(e, totalPages - 1)}>{totalPages}</a>);
    }

    return pages;
  }

  render() {
    return (
      <div className="pagination">
        <ul>
          <li key="previous">
            <Link className={`prev ${this.currentPage === 0 ? 'disabled' : ''}`}
              href={this.getUrl(this.currentPage - 1)}
              onClick={e => this.onClick(e, this.currentPage - 1)}
            >
              <Icon
                name="ico-arrow-circle-left"
                size={200}
              />
            </Link>
          </li>
          {this.state.isMobile ? (
            <li className="mobile-indication">
              <div>{this.currentPage + 1} <Text>pagination.label.to</Text> {this.totalPages}</div>
            </li>
          ) : (
            <Fragment>
              {this.pages.map((page, i) => <li key={i} className="page">{page}</li>)}
            </Fragment>
          )}
          <li key="next">
            <Link className={`next ${(this.currentPage === this.totalPages - 1) ? 'disabled' : ''}`}
              href={this.getUrl(this.currentPage + 1)}
              onClick={e => this.onClick(e, this.currentPage + 1)}
            >
              <Icon
                name="ico-arrow-circle-right"
                size={200}
              />
            </Link>
          </li>
        </ul>
      </div>
    );
  }
}

Pagination.contextType = Context;
Pagination.propTypes = {
  /**
   * The callback function to pass the new pagenumber
   */
  callBack: PropTypes.func,

  /**
   * What to use a the query var key
   */
  value: PropTypes.string,

  /**
   * Current selected page number
   */
  currentPage: PropTypes.number.isRequired,

  /**
   * Total number of pages in result list
   */
  totalPages: PropTypes.number.isRequired,
  /**
   * Request url for performing subsequent page requests (used only if component is loaded statically without a container)
   */
  url: PropTypes.string,
  store: PropTypes.object,
};

export default Pagination;
